import { ImageUpload } from 'components/forms/ImageUpload';
import { useFormikContext } from 'formik';
import { useCallback } from 'react';

interface FormikUploadImageProps<T extends Record<string, any>> {
  fieldName: keyof T & string;
  label?: string;
  required?: boolean;
  placeholder: string;
  dragPlaceholder: string;
  height?: string;
  maxSizeInMB?: number;
  acceptedTypes?: string[];
  className?: string;
  /** Image already stored on the entity: shown while no new file is picked. */
  previewUrl?: string | null;
}

export const FormikUploadImage = <T extends Record<string, any>>({
  fieldName,
  label,
  required,
  placeholder,
  dragPlaceholder,
  height = "h-64",
  maxSizeInMB = 5,
  acceptedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
  className = "",
  previewUrl
}: FormikUploadImageProps<T>) => {
  const { setFieldValue, values, errors, setFieldError } = useFormikContext<any>();

  const handlePhotoChange = useCallback((file: File | null) => {
    setFieldValue(fieldName, file);
    // Clear l'erreur si un fichier est sélectionné
    if (file && errors[fieldName]) {
      setFieldError(fieldName, undefined);
    }
  }, [fieldName, errors, setFieldValue, setFieldError]);

  const handlePhotoError = useCallback((error: string) => {
    setFieldError(fieldName, error);
  }, [fieldName, setFieldError]);

  return (
    <div>
      <ImageUpload
        value={values[fieldName]}
        /* A freshly picked file wins over the stored image; dropping it falls
           back to the stored one, which stays untouched server side. */
        previewUrl={values[fieldName] ? undefined : previewUrl}
        onChange={handlePhotoChange}
        onError={handlePhotoError}
        label={label}
        required={required}
        placeholder={placeholder}
        dragPlaceholder={dragPlaceholder}
        height={height}
        maxSizeInMB={maxSizeInMB}
        acceptedTypes={acceptedTypes}
        className={className}
      />

      {/* Affichage de l'erreur Formik si elle existe */}
      {errors[fieldName] && (
        <div className="text-red-600 text-sm mt-1">
          {errors[fieldName] as string}
        </div>
      )}
    </div>
  );
};
